<template>
  <div class="pdp-set-picker">
    <p
      v-if="referenceMode"
      class="pdp-set-picker__intro"
    >
      I built one row per reference image — each
      <b><i class="bi bi-stars pdp-set-picker__intro-icon" /> AI</b>
      tile shows the source it'll be based on. Reorder, remove, or add rows, then
      create your set.
    </p>
    <p
      v-else
      class="pdp-set-picker__intro"
    >
      For each image type, pick
      <b><i class="bi bi-stars pdp-set-picker__intro-icon" /> AI</b>
      to generate a fresh image or
      <b><i class="bi bi-image pdp-set-picker__intro-icon" /> Gallery</b>
      to use a pre-designed layout. I've pre-selected
      {{ initialRowCount }} recommended types — all set to AI by default. Click
      the Gallery tile on any row to swap that one.
    </p>

    <div
      v-if="!referenceMode"
      class="pdp-set-picker__preset"
      :class="{ 'is-open': presetOpen }"
    >
      <button
        type="button"
        class="pdp-set-picker__preset-trigger"
        :disabled="disabled"
        @click="togglePresetDropdown"
      >
        <i class="bi bi-soundwave pdp-set-picker__preset-icon" />
        <span class="pdp-set-picker__preset-label">Start from a sequence</span>
        <span class="pdp-set-picker__preset-value">{{ activePresetLabel || 'Choose a preset...' }}</span>
        <i class="bi bi-chevron-down pdp-set-picker__preset-chevron" />
      </button>
      <div
        v-show="presetOpen"
        class="pdp-set-picker__preset-menu"
      >
        <button
          v-for="src in presetSources"
          :key="src.id"
          type="button"
          class="pdp-set-picker__preset-item"
          :class="{ 'is-active': src.id === activePresetId }"
          @click="selectPreset(src.id)"
        >
          <span>{{ src.label }} <span class="pdp-set-picker__preset-count">· {{ src.count }}</span></span>
          <i
            v-if="src.id === activePresetId"
            class="bi bi-check-lg"
          />
        </button>
      </div>
    </div>

    <div class="pdp-set-picker__bulk">
      <span class="pdp-set-picker__bulk-label">Set all to:</span>
      <button
        type="button"
        class="pdp-set-picker__bulk-btn"
        :disabled="disabled"
        @click="setAll('ai')"
      >
        <i class="bi bi-stars" /> AI
      </button>
      <button
        type="button"
        class="pdp-set-picker__bulk-btn"
        :disabled="disabled || !anyTemplatesAvailable"
        @click="setAll('gallery')"
      >
        <i class="bi bi-image" /> Gallery
      </button>
    </div>

    <div
      ref="rowsList"
      class="pdp-set-picker__rows"
    >
      <div
        v-for="(row, rowIdx) in localRows"
        :key="row._uid"
        class="pdp-set-picker__row"
      >
        <div class="pdp-set-picker__row-actions">
          <span class="drag-handle pdp-set-picker__row-action">
            <GripVerticalIcon
              :width="16"
              :height="16"
            />
          </span>
        </div>
        <div class="pdp-set-picker__row-label">
          <span>{{ row.label }}</span>
          <button
            type="button"
            class="pdp-set-picker__row-action pdp-set-picker__row-action--remove"
            :disabled="disabled"
            :aria-label="`Remove ${row.label} row`"
            @click="removeRow(rowIdx)"
          >
            <i class="bi bi-x-lg" />
          </button>
        </div>
        <div class="pdp-set-picker__row-tiles-wrapper">
          <button
            v-show="rowOverflow[rowIdx] && rowOverflow[rowIdx].canScrollLeft"
            type="button"
            class="pdp-set-picker__row-chevron pdp-set-picker__row-chevron--left"
            aria-label="Scroll left"
            @click="scrollRow(rowIdx, -1)"
          >
            <i class="bi bi-chevron-left" />
          </button>
          <div
            ref="rowTiles"
            class="pdp-set-picker__row-tiles"
            @scroll="onRowScroll(rowIdx)"
          >
            <template v-if="isHeroRow(row)">
              <div class="pdp-set-picker__tile-slot">
                <button
                  type="button"
                  class="pdp-set-picker__tile pdp-set-picker__tile--original"
                  :class="{ 'is-selected': row.mode === 'original' }"
                  :title="row.label"
                  :disabled="disabled"
                  @click="setRowMode(rowIdx, 'original')"
                >
                  <img
                    v-if="heroImageFor(row)"
                    :src="heroImageFor(row)"
                    :alt="row.label"
                  >
                  <i
                    v-else
                    class="bi bi-image"
                  />
                  <span class="pdp-set-picker__tile-original-label">Original</span>
                  <i
                    v-if="row.mode === 'original'"
                    class="bi bi-check-circle-fill pdp-set-picker__tile-check"
                  />
                </button>
                <button
                  v-if="heroImageFor(row)"
                  type="button"
                  class="pdp-set-picker__tile-expand"
                  aria-label="Preview image"
                  :disabled="disabled"
                  @click.stop="openImagePreview(heroImageFor(row), row.label)"
                >
                  <i class="bi bi-arrows-angle-expand" />
                </button>
              </div>
              <button
                type="button"
                class="pdp-set-picker__tile pdp-set-picker__tile--ai pdp-set-picker__tile--hero-alt"
                :class="{ 'is-selected': row.mode === 'ai' }"
                :disabled="disabled"
                @click="setRowMode(rowIdx, 'ai')"
              >
                <i class="bi bi-stars" />
                <span>AI</span>
                <i
                  v-if="row.mode === 'ai'"
                  class="bi bi-check-circle-fill pdp-set-picker__tile-check"
                />
              </button>
            </template>
            <div
              v-if="!isHeroRow(row)"
              class="pdp-set-picker__tile-slot"
            >
              <button
                type="button"
                class="pdp-set-picker__tile pdp-set-picker__tile--ai"
                :class="{ 'is-selected': row.mode === 'ai', 'pdp-set-picker__tile--ai-ref': !!row.reference_image_url }"
                :disabled="disabled"
                @click="setRowMode(rowIdx, 'ai')"
              >
                <template v-if="row.reference_image_url">
                  <img
                    :src="row.reference_image_url"
                    :alt="row.label"
                    class="pdp-set-picker__tile-ref-img"
                  >
                  <span class="pdp-set-picker__tile-ref-badge">
                    <i class="bi bi-stars" /> AI
                  </span>
                </template>
                <template v-else>
                  <i class="bi bi-stars" />
                  <span>AI</span>
                </template>
                <i
                  v-if="row.mode === 'ai'"
                  class="bi bi-check-circle-fill pdp-set-picker__tile-check"
                />
              </button>
              <button
                v-if="row.reference_image_url"
                type="button"
                class="pdp-set-picker__tile-expand"
                aria-label="Preview image"
                :disabled="disabled"
                @click.stop="openImagePreview(row.reference_image_url, row.label)"
              >
                <i class="bi bi-arrows-angle-expand" />
              </button>
            </div>

            <button
              v-if="row.type === 'lifestyle'"
              type="button"
              class="pdp-set-picker__tile pdp-set-picker__tile--scene"
              :class="{ 'is-selected': row.mode === 'scene' }"
              :disabled="disabled"
              @click="setRowMode(rowIdx, 'scene')"
            >
              <i class="bi bi-image-alt" />
              <span>Scene</span>
              <i
                v-if="row.mode === 'scene'"
                class="bi bi-check-circle-fill pdp-set-picker__tile-check"
              />
            </button>

            <div
              v-for="tpl in templatesForRow(row)"
              :key="`${row.rowIndex}-${tpl.id}`"
              class="pdp-set-picker__tile pdp-set-picker__tile--tpl"
              :class="{
                'is-selected': row.mode === 'gallery' && row.template_id === tpl.id,
                'is-disabled': disabled,
              }"
              role="button"
              :tabindex="disabled ? -1 : 0"
              :aria-pressed="row.mode === 'gallery' && row.template_id === tpl.id"
              :aria-disabled="disabled"
              @click="onTemplateTileClick(rowIdx, tpl)"
              @keydown.enter.prevent="onTemplateTileClick(rowIdx, tpl)"
              @keydown.space.prevent="onTemplateTileClick(rowIdx, tpl)"
            >
              <img
                :src="tpl.thumbnail"
                :alt="tpl.name"
              >
              <i
                v-if="row.mode === 'gallery' && row.template_id === tpl.id"
                class="bi bi-check-circle-fill pdp-set-picker__tile-check"
              />
              <button
                type="button"
                class="pdp-set-picker__tile-expand"
                aria-label="Preview template"
                :disabled="disabled"
                @click.stop="openLightbox(rowIdx, tpl)"
              >
                <i class="bi bi-arrows-angle-expand" />
              </button>
            </div>
          </div>
          <button
            v-show="rowOverflow[rowIdx] && rowOverflow[rowIdx].canScrollRight"
            type="button"
            class="pdp-set-picker__row-chevron pdp-set-picker__row-chevron--right"
            aria-label="Scroll right"
            @click="scrollRow(rowIdx, 1)"
          >
            <i class="bi bi-chevron-right" />
          </button>
        </div>
        <div
          class="pdp-set-picker__yours-wrap"
          :class="{
            'pdp-set-picker__hero-alt-wrap': isHeroRow(row),
            'is-selected': row.mode === 'yours',
            'is-menu-open': yoursMenuIdx === rowIdx,
          }"
        >
          <button
            type="button"
            class="pdp-set-picker__tile pdp-set-picker__tile--yours"
            :class="{ 'is-selected': row.mode === 'yours' }"
            title="Use your own image (upload, File Browser, or Salsify)"
            :disabled="disabled"
            @click.stop="toggleYoursMenu(rowIdx)"
          >
            <template v-if="row.mode === 'yours' && (row.own_image_thumb || row.own_image_url)">
              <img
                :src="row.own_image_thumb || row.own_image_url"
                :alt="row.label"
              >
              <i class="bi bi-check-circle-fill pdp-set-picker__tile-check" />
            </template>
            <template v-else>
              <i class="bi bi-upload" />
              <span>Yours</span>
            </template>
          </button>
          <button
            v-if="row.mode === 'yours' && (row.own_image_thumb || row.own_image_url)"
            type="button"
            class="pdp-set-picker__tile-expand"
            aria-label="Preview image"
            :disabled="disabled"
            @click.stop="openImagePreview(row.own_image_url || row.own_image_thumb, row.label)"
          >
            <i class="bi bi-arrows-angle-expand" />
          </button>
          <div
            v-if="yoursMenuIdx === rowIdx"
            class="pdp-set-picker__yours-menu"
            @click.stop
          >
            <div class="pdp-set-picker__yours-menu-head">
              USE YOUR OWN IMAGE
            </div>
            <button
              type="button"
              class="pdp-set-picker__yours-menu-item"
              :disabled="disabled"
              @click="requestOwnImage(rowIdx, 'upload')"
            >
              <i class="bi bi-upload" /> Upload from computer...
            </button>
            <button
              type="button"
              class="pdp-set-picker__yours-menu-item"
              :disabled="disabled"
              @click="requestOwnImage(rowIdx, 'browser')"
            >
              <i class="bi bi-folder2-open" /> Choose from File Browser...
            </button>
            <button
              type="button"
              class="pdp-set-picker__yours-menu-item"
              :disabled="disabled"
              @click="requestOwnImage(rowIdx, 'salsify')"
            >
              <i class="bi bi-bar-chart-steps" /> Pull from Salsify...
            </button>
          </div>
        </div>
        <span
          v-if="isHeroRow(row) && row.mode === 'original'"
          class="pdp-set-picker__original-note"
        >Used as-is — no AI changes</span>
        <button
          v-if="!isHeroRow(row)"
          type="button"
          class="pdp-set-picker__tile pdp-set-picker__tile--add"
          :disabled="disabled"
          aria-label="Open Design Gallery"
          @click="$emit('open-gallery')"
        >
          <i class="bi bi-plus-lg" />
        </button>
        <div class="pdp-set-picker__row-spacer" />
        <div
          v-if="isGenerativeRow(row)"
          class="pdp-set-picker__wand-wrap"
        >
          <button
            type="button"
            class="pdp-set-picker__wand"
            :class="{ 'is-active': hasDirection(row, rowIdx), 'is-open': directionOpenIdx === rowIdx }"
            title="Add design direction (optional)"
            :disabled="disabled"
            @click.stop="toggleDirection(rowIdx, $event)"
          >
            <i class="bi bi-magic" />
            <span
              v-if="hasDirection(row, rowIdx)"
              class="pdp-set-picker__wand-dot"
            />
          </button>
          <div
            v-if="directionOpenIdx === rowIdx"
            class="pdp-set-picker__direction"
            :class="{ 'is-above': directionAbove[rowIdx] }"
            @click.stop
          >
            <div class="pdp-set-picker__direction-head">
              <span class="pdp-set-picker__direction-title">Customize {{ row.label }}</span>
              <span class="pdp-set-picker__direction-optional">All optional</span>
            </div>
            <div class="pdp-set-picker__direction-section-head">
              <span class="pdp-set-picker__direction-label">Direction</span>
            </div>
            <textarea
              class="pdp-set-picker__direction-text"
              rows="3"
              :placeholder="directionPlaceholderFor(row)"
              :value="row.userInstructions || ''"
              :disabled="disabled"
              @input="setRowDirection(rowIdx, $event.target.value)"
            />
            <div class="pdp-set-picker__direction-section">
              <div class="pdp-set-picker__direction-section-head">
                <FileTextIcon
                  :width="12"
                  :height="12"
                />
                <span class="pdp-set-picker__direction-label">Overlay text</span>
              </div>
              <textarea
                class="pdp-set-picker__direction-text"
                rows="3"
                :placeholder="overlayTextPlaceholder"
                :value="row.overlayText || ''"
                :disabled="disabled"
                @input="setRowOverlayText(rowIdx, $event.target.value)"
              />
              <div class="pdp-set-picker__overlay-mode">
                <label
                  class="pdp-set-picker__overlay-mode-btn"
                  :class="{ 'is-active': (row.overlayTextMode || 'editable') === 'editable' }"
                >
                  <input
                    type="radio"
                    :name="`overlay-mode-${rowIdx}`"
                    value="editable"
                    :checked="(row.overlayTextMode || 'editable') === 'editable'"
                    :disabled="disabled"
                    @change="setRowOverlayTextMode(rowIdx, 'editable')"
                  >
                  Editable layer
                </label>
                <label
                  class="pdp-set-picker__overlay-mode-btn"
                  :class="{ 'is-active': row.overlayTextMode === 'part_of_image' }"
                >
                  <input
                    type="radio"
                    :name="`overlay-mode-${rowIdx}`"
                    value="part_of_image"
                    :checked="row.overlayTextMode === 'part_of_image'"
                    :disabled="disabled"
                    @change="setRowOverlayTextMode(rowIdx, 'part_of_image')"
                  >
                  Part of image
                </label>
              </div>
            </div>
            <div class="pdp-set-picker__direction-section">
              <div class="pdp-set-picker__direction-section-head">
                <ImageIcon
                  :width="12"
                  :height="12"
                />
                <span class="pdp-set-picker__direction-label">Brand logo</span>
                <button
                  v-if="brandLogoByRowIdx[rowIdx]"
                  type="button"
                  class="pdp-set-picker__logo-remove"
                  @click="removeBrandLogo(rowIdx)"
                >
                  Remove
                </button>
              </div>
              <div
                v-if="brandLogoByRowIdx[rowIdx]"
                class="pdp-set-picker__logo-selected"
              >
                <img
                  :src="brandLogoByRowIdx[rowIdx].url"
                  class="pdp-set-picker__logo-thumb-sm"
                >
                <span class="pdp-set-picker__logo-name">{{ brandLogoByRowIdx[rowIdx].name || 'Logo' }}</span>
                <button
                  type="button"
                  class="pdp-set-picker__logo-change"
                  :disabled="disabled"
                  @click="requestBrandLogo(rowIdx)"
                >
                  Change
                </button>
              </div>
              <button
                v-else
                type="button"
                class="pdp-set-picker__logo-btn"
                :disabled="disabled"
                @click="requestBrandLogo(rowIdx)"
              >
                <FolderOpenIcon
                  :width="14"
                  :height="14"
                />
                Choose from Files
              </button>
            </div>
            <div class="separator w-100" />
            <div class="pdp-set-picker__direction-foot">
              <span class="pdp-set-picker__direction-note">Saved automatically · applies only to this image.</span>
              <button
                type="button"
                class="pdp-set-picker__direction-done"
                @click="directionOpenIdx = null"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="addableTypes.length"
      class="pdp-set-picker__add-image-type"
    >
      <button
        type="button"
        class="pdp-set-picker__add-trigger"
        :class="{ 'is-open': addableTypesOpen }"
        :disabled="disabled"
        @click="toggleAddableTypesDropdown"
      >
        <i class="bi bi-plus-lg" /> Add image type
        <span>({{ addableTypes.length }})</span>
        <i class="bi bi-chevron-down pdp-set-picker__add-chevron" />
      </button>
      <div
        v-if="addableTypesOpen"
        class="pdp-set-picker__add-dropdown-menu"
      >
        <button
          v-for="t in addableTypes"
          :key="t.key"
          type="button"
          class="pdp-set-picker__add-item"
          @click="addRow(t)"
        >
          + {{ t.label }}
        </button>
      </div>
    </div>

    <div
      ref="footer"
      class="pdp-set-picker__footer"
    >
      <button
        type="button"
        class="btn btn-primary pdp-set-picker__create"
        :disabled="disabled || !localRows.length"
        @click="confirm('parallel')"
      >
        <i class="bi bi-lightning-charge-fill" />
        Create {{ localRows.length }} at once →
      </button>
      <button
        type="button"
        class="pdp-set-picker__create-sequential"
        :disabled="disabled || !localRows.length"
        @click="confirm('sequential')"
      >
        One at a time
      </button>
      <button
        type="button"
        class="pdp-set-picker__browse"
        :disabled="disabled"
        @click="$emit('open-gallery')"
      >
        Browse full gallery →
      </button>
    </div>

    <AIChatWidgetPdpTemplateLightbox
      :visible="!!lightbox.visible"
      :template="lightbox.template || {}"
      :is-selected="lightbox.isSelected"
      @select-template="onLightboxSelect"
      @deselect-template="onLightboxDeselect"
      @close="closeLightbox"
    />

    <div
      v-if="imagePreview.visible"
      class="pdp-set-picker__preview-lightbox"
      @click.self="closeImagePreviewModal"
    >
      <button
        type="button"
        class="pdp-set-picker__preview-close"
        aria-label="Close"
        @click="closeImagePreviewModal"
      >
        <i class="bi bi-x-lg" />
      </button>
      <img
        :src="imagePreview.url"
        :alt="imagePreview.label"
        class="pdp-set-picker__preview-img"
      >
    </div>
  </div>
</template>

<script>
import Sortable from 'sortablejs';
import { BUILT_IN_TYPES, reindexRowSelections } from '@frontend/store/creative-studio/chat-function-handlers/pdp-generation-paths';
import AIChatWidgetPdpTemplateLightbox from './AIChatWidgetPdpTemplateLightbox.vue';
import {
  FileText as FileTextIcon,
  Image as ImageIcon,
  FolderOpen as FolderOpenIcon,
  GripVertical as GripVerticalIcon
} from "lucide-vue";

let rowUidSeq = 0;

function nextRowUid() {
  rowUidSeq += 1;
  return `pdp-row-${rowUidSeq}`;
}

export default {
  name: 'AIChatWidgetPdpImageSetPickerLegacy',
  components: {
    AIChatWidgetPdpTemplateLightbox,
    FileTextIcon,
    ImageIcon,
    FolderOpenIcon,
    GripVerticalIcon
  },
  props: {
    rows: { type: Array, required: true },
    templatesBySubtype: { type: Object, default: () => ({}) },
    presetSources: { type: Array, default: () => [] },
    initialPresetId: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    referenceMode: { type: Boolean, default: false },
    productImageUrl: { type: String, default: '' },
    ownImageResult: { type: Object, default: null },
    brandLogoResult: { type: Object, default: null },
  },
  data() {
    return {
      localRows: this.rows.map((r) => ({ overlayTextMode: 'editable', ...r, _uid: r._uid || nextRowUid() })),
      initialRowCount: this.rows.length,
      activePresetId: this.initialPresetId || (this.presetSources[0] ? this.presetSources[0].id : ''),
      presetOpen: false,
      addableTypesOpen: false,
      lightbox: { visible: false, rowIdx: null, template: null, isSelected: false },
      imagePreview: { visible: false, url: '', label: '' },
      rowOverflow: {},
      directionOpenIdx: null,
      directionAbove: {},
      pendingLogoDirectionIdx: null,
      pendingLogoRowIdx: null,
      brandLogoByRowIdx: {},
      yoursMenuIdx: null,
      sortable: null,
      directionPlaceholder: 'e.g. "warm afternoon kitchen, soft shadows" or "minimal white background, dramatic angle"',
      overlayTextPlaceholder: 'e.g. "Now 20% off" — kept as a separate text layer',
      generationMode: (typeof window !== 'undefined' && window.localStorage && window.localStorage.getItem('cs_pdp_generation_mode')) || 'parallel',
    };
  },
  computed: {
    activePresetLabel() {
      const src = this.presetSources.find((s) => s.id === this.activePresetId);
      return src ? src.label : '';
    },
    anyTemplatesAvailable() {
      return Object.values(this.templatesBySubtype || {}).some((arr) => arr && arr.length);
    },
    addableTypes() {
      const present = new Set(this.localRows.map((r) => r.type));
      return Object.values(BUILT_IN_TYPES)
        .filter((t) => !present.has(t.key))
        .sort((a, b) => a.label.localeCompare(b.label));
    },
  },
  watch: {
    localRows() {
      this.measureAllRowOverflow();

      if (this.addableTypes.length === 0) {
        this.addableTypesOpen = false;
      }
    },
    templatesBySubtype() {
      this.measureAllRowOverflow();
    },
    disabled(val) {
      if (this.sortable) {
        this.sortable.option('disabled', val);
      }
    },
    brandLogoResult(res) {
      if (!res || !res.url) { return; }

      const idx = this.pendingLogoRowIdx !== null
        ? this.pendingLogoRowIdx
        : this.localRows.findIndex((r) => r.rowIndex === res.rowIndex);

      if (idx === -1 || idx === null) { return; }

      this.$set(this.brandLogoByRowIdx, idx, { url: res.url, name: res.name || '' });

      this.pendingLogoRowIdx = null;

      if (this.pendingLogoDirectionIdx !== null) {
        this.directionOpenIdx = this.pendingLogoDirectionIdx;
        this.pendingLogoDirectionIdx = null;
      }
    },
    // The parent resolved a user-supplied image (upload / File Browser / Salsify)
    // and wrote it back. Apply it to the target row as a use-as-is "yours" image.
    // `nonce` on the result guarantees the watcher fires on repeat picks. (POR-13271)
    ownImageResult(res) {
      if (!res || !res.url) { return; }

      const idx = this.localRows.findIndex((r) => r.rowIndex === res.rowIndex);

      if (idx === -1) { return; }

      this.$set(this.localRows, idx, {
        ...this.localRows[idx],
        mode: 'yours',
        own_image_url: res.url,
        own_image_thumb: res.thumb || res.url,
        own_image_source: res.source || null,
        template_id: null,
        template_thumb: null,
        template_image_url: null,
        scene: null,
      });
    },
  },
  created() {
    // Lock hero rows to the "Original" (use-as-is) mode on init. Idempotent, so
    // it's safe across remounts (reload) where rows already carry mode:'original'.
    this.localRows = this.localRows.map((r) => this.withHeroMode(r));
  },
  mounted() {
    document.addEventListener('mousedown', this.onDocumentMousedown);
    document.addEventListener('keydown', this.onDocumentKeydown);
    this.measureAllRowOverflow();
    window.addEventListener('resize', this.measureAllRowOverflow);

    this.$nextTick(() => this.initSortable());
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onDocumentMousedown);
    document.removeEventListener('keydown', this.onDocumentKeydown);
    window.removeEventListener('resize', this.measureAllRowOverflow);

    if (this.sortable) {
      this.sortable.destroy();
    }
  },
  methods: {
    templatesForRow(row) {
      if (this.isHeroRow(row)) return [];
      return (this.templatesBySubtype && this.templatesBySubtype[row.type]) || [];
    },
    // Identifies a hero row. It defaults to "Original" (use-as-is) but can be
    // switched to AI or Yours on hover; this flag only controls the Original tile
    // + the no-gallery/scene/wand/+ treatment, not the selected mode. (POR-13271)
    isHeroRow(row) {
      return !!row && row.type === 'hero';
    },
    heroImageFor(row) {
      return (row && row.original_image_url) || this.productImageUrl || '';
    },
    withHeroMode(row) {
      if (this.isHeroRow(row)) {
        const heroImage = row.original_image_url || this.productImageUrl || null;
        // Hero defaults to the product packshot shown "Original" (use-as-is).
        // But with no packshot available (e.g. a brief opened without a product),
        // locking Hero to 'original' leaves a permanently-blank tile that never
        // generates — so fall back to AI generation like the other rows. The
        // user can still switch to Original/Yours once a product image exists.
        if (heroImage) {
          return { ...row, mode: 'original', original_image_url: heroImage };
        }
        return { ...row, mode: 'ai', original_image_url: null };
      }
      return row;
    },
    // Per-image direction (the wand) only applies to rows that actually run a
    // prompt — AI and Scene. Not Hero-original, gallery, or "Yours". (POR-13271)
    isGenerativeRow(row) {
      return !!row && (row.mode === 'ai' || row.mode === 'scene') && !this.isHeroRow(row);
    },
    hasDirection(row, rowIdx) {
      return !!(row && (
        (row.userInstructions && row.userInstructions.trim()) ||
        (row.overlayText && row.overlayText.trim()) ||
        (rowIdx !== undefined ? this.brandLogoByRowIdx[rowIdx] : null)
      ));
    },
    // Per-type guidance for the direction wand. The new gallery / cross-sell /
    // iconography types depend on user-supplied specifics (background, overlay
    // text, benefit-icon list), so each shows a tailored example; everything
    // else keeps the generic default. (POR-13366)
    directionPlaceholderFor(row) {
      const byType = {
        product_gallery: 'Describe the background, tagline/logo and overlay text (e.g. neutral linen backdrop; brand logo + tagline; overlay "Same pure formula, fresh new look")',
        cross_sell: 'Add overlay text / call-to-action and any styling notes (e.g. "Find your ritual")',
        category_gallery: 'Describe the lifestyle scene + overlay text (e.g. serene bathroom with the collection displayed; overlay "Elevate your self-care")',
        feature_iconography_around: 'List the benefit icons to integrate (e.g. Longest Lasting, Sensory Experience, Sustainable, Vegan, Nourishing, Luxury)',
        feature_iconography_bottom: 'List the benefit icons to integrate (e.g. Longest Lasting, Sustainable, Vegan, Nourishing)',
      };
      return (row && byType[row.type]) || this.directionPlaceholder;
    },
    toggleDirection(rowIdx, event) {
      if (this.disabled) return;
      this.yoursMenuIdx = null;
      this.directionOpenIdx = this.directionOpenIdx === rowIdx ? null : rowIdx;
      if (this.directionOpenIdx === rowIdx && event) {
        const rect = event.currentTarget.getBoundingClientRect();
        this.$set(this.directionAbove, rowIdx, window.innerHeight - rect.bottom < 420);
      }
    },
    setRowDirection(rowIdx, value) {
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], userInstructions: value });
    },
    clearRowDirection(rowIdx) {
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], userInstructions: '' });
    },
    setRowOverlayText(rowIdx, value) {
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], overlayText: value });
    },
    setRowOverlayTextMode(rowIdx, mode) {
      this.$set(this.localRows, rowIdx, { ...this.localRows[rowIdx], overlayTextMode: mode });
    },
    requestBrandLogo(rowIdx) {
      if (this.disabled) { return; }

      this.pendingLogoDirectionIdx = this.directionOpenIdx;
      this.pendingLogoRowIdx = rowIdx;

      this.$emit('brand-logo-request', { rowIndex: this.localRows[rowIdx].rowIndex });
    },
    removeBrandLogo(rowIdx) {
      this.$set(this.brandLogoByRowIdx, rowIdx, null);
    },
    toggleYoursMenu(rowIdx) {
      if (this.disabled) return;
      this.directionOpenIdx = null;
      this.yoursMenuIdx = this.yoursMenuIdx === rowIdx ? null : rowIdx;
    },
    requestOwnImage(rowIdx, source) {
      this.yoursMenuIdx = null;
      // The parent (NetworksPageV2) resolves the URL (upload / File Browser /
      // Salsify) and writes it back via the `ownImageResult` prop. (POR-13271)
      this.$emit('own-image-request', { rowIndex: this.localRows[rowIdx].rowIndex, source });
    },
    togglePresetDropdown() {
      if (this.disabled) return;
      this.presetOpen = !this.presetOpen;
    },
    toggleAddableTypesDropdown() {
      if (this.disabled) {
        return;
      }

      this.addableTypesOpen = !this.addableTypesOpen;
    },
    onDocumentMousedown(event) {
      const inside = this.$el && this.$el.contains(event.target);
      if (!inside) {
        this.presetOpen = false;
        this.addableTypesOpen = false;
        this.directionOpenIdx = null;
        this.yoursMenuIdx = null;
        return;
      }
      // Click inside the widget but outside an open direction popover / yours
      // menu (e.g. another row or a tile) closes those poppers. The triggers and
      // popovers themselves are whitelisted so their own mousedown doesn't close
      // them before the click toggles. (POR-13271)
      const keep = event.target.closest && event.target.closest(
        '.pdp-set-picker__direction, .pdp-set-picker__wand, .pdp-set-picker__yours-menu, .pdp-set-picker__tile--yours',
      );
      if (!keep) {
        this.directionOpenIdx = null;
        this.yoursMenuIdx = null;
      }

      const inAddArea = event.target.closest && event.target.closest(
        '.pdp-set-picker__add-trigger, .pdp-set-picker__add-dropdown-menu',
      );

      if (!inAddArea) {
        this.addableTypesOpen = false;
      }
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape') {
        this.presetOpen = false;
        this.addableTypesOpen = false;
        this.directionOpenIdx = null;
        this.yoursMenuIdx = null;
      }
    },
    selectPreset(id) {
      this.activePresetId = id;
      this.presetOpen = false;
      const src = this.presetSources.find((s) => s.id === id);
      if (!src) return;
      const newRows = (src.slots || []).map((slot, i) => ({
        rowIndex: i,
        _uid: nextRowUid(),
        type: slot.key,
        label: slot.label,
        mode: 'ai',
        template_id: null,
        template_thumb: null,
        scene: null,
        productMode: null,
        overlayTextMode: 'editable',
      }));
      this.localRows = newRows.map((r) => this.withHeroMode(r));
    },
    setAll(mode) {
      if (mode === 'ai') {
        this.localRows = this.localRows.map((r) => (this.isHeroRow(r) ? r : {
          ...r,
          mode: 'ai',
          template_id: null,
          template_thumb: null,
          scene: null,
        }));
        return;
      }
      if (mode === 'gallery') {
        this.localRows = this.localRows.map((r) => {
          if (this.isHeroRow(r)) return r;
          const tpls = this.templatesForRow(r);
          if (!tpls.length) return r;
          const first = tpls[0];
          return {
            ...r,
            mode: 'gallery',
            template_id: first.id,
            template_thumb: first.thumbnail,
            template_image_url: first.image_url || first.thumbnail || null,
            scene: null,
          };
        });
      }
    },
    setRowMode(rowIdx, mode) {
      const row = { ...this.localRows[rowIdx], mode };
      if (mode !== 'gallery') {
        row.template_id = null;
        row.template_thumb = null;
        row.template_image_url = null;
      }
      if (mode !== 'scene') {
        row.scene = null;
      }
      if (mode === 'original') {
        // Switching a hero back to "Original" must restore the use-as-is packshot.
        row.original_image_url = row.original_image_url || this.productImageUrl || null;
      }
      this.$set(this.localRows, rowIdx, row);
    },
    setRowTemplate(rowIdx, tpl) {
      const row = {
        ...this.localRows[rowIdx],
        mode: 'gallery',
        template_id: tpl.id,
        template_thumb: tpl.thumbnail,
        template_image_url: tpl.image_url || tpl.thumbnail || null,
        scene: null,
      };
      this.$set(this.localRows, rowIdx, row);
    },
    onTemplateTileClick(rowIdx, tpl) {
      if (this.disabled) return;
      this.setRowTemplate(rowIdx, tpl);
    },
    addRow(typeDef) {
      const newRow = {
        rowIndex: this.localRows.length,
        _uid: nextRowUid(),
        type: typeDef.key,
        label: typeDef.label,
        mode: 'ai',
        template_id: null,
        template_thumb: null,
        scene: null,
        productMode: null,
        overlayTextMode: 'editable',
      };
      this.localRows = reindexRowSelections([...this.localRows, this.withHeroMode(newRow)]);

      this.$nextTick(() => {
        const anchor = this.$refs.footer;

        if (!anchor) {
          return;
        }

        anchor.style.scrollMarginBottom = '120px';

        anchor.scrollIntoView({ behavior: 'smooth', block: 'end' });
      });
    },
    removeRow(rowIdx) {
      if (this.disabled) return;
      const next = this.localRows.slice();
      next.splice(rowIdx, 1);
      this.localRows = reindexRowSelections(next);
    },
    initSortable() {
      const el = this.$refs.rowsList;

      if (!el) {
        return;
      }

      if (this.sortable) {
        this.sortable.destroy();
      }

      this.sortable = Sortable.create(el, {
        handle: '.drag-handle',
        draggable: '.pdp-set-picker__row',
        animation: 150,
        disabled: this.disabled,
        onEnd: this.onRowSortEnd,
      });
    },
    onRowSortEnd({ oldIndex, newIndex }) {
      if (this.disabled || oldIndex === newIndex || oldIndex == null || newIndex == null) {
        return;
      }

      const next = this.localRows.slice();
      const [moved] = next.splice(oldIndex, 1);
      next.splice(newIndex, 0, moved);
      this.localRows = reindexRowSelections(next);
    },
    openLightbox(rowIdx, tpl) {
      const row = this.localRows[rowIdx];
      this.lightbox = {
        visible: true,
        rowIdx,
        template: tpl,
        isSelected: row.mode === 'gallery' && row.template_id === tpl.id,
      };
    },
    closeLightbox() {
      this.lightbox = { visible: false, rowIdx: null, template: null, isSelected: false };
    },
    onLightboxSelect(tpl) {
      if (this.lightbox.rowIdx !== null) this.setRowTemplate(this.lightbox.rowIdx, tpl);
      this.closeLightbox();
    },
    onLightboxDeselect() {
      if (this.lightbox.rowIdx !== null) this.setRowMode(this.lightbox.rowIdx, 'ai');
      this.closeLightbox();
    },
    openImagePreview(url, label) {
      if (!url) return;
      this.imagePreview = { visible: true, url, label: label || '' };
    },
    closeImagePreviewModal() {
      this.imagePreview = { visible: false, url: '', label: '' };
    },
    confirm(mode) {
      const generationMode = mode || this.generationMode || 'parallel';
      try {
        if (window.localStorage) window.localStorage.setItem('cs_pdp_generation_mode', generationMode);
      } catch (e) { /* ignore */ }

      const rowsWithLogos = this.localRows.map((r, i) => {
        const logo = this.brandLogoByRowIdx[i];
        return logo ? { ...r, brandLogoUrl: logo.url, brandLogoName: logo.name } : r;
      });

      this.$emit('confirm', {
        rowSelections: reindexRowSelections(rowsWithLogos),
        presetId: this.activePresetId,
        generationMode,
      });
    },
    measureRowOverflow(rowIdx) {
      const refs = this.$refs.rowTiles;
      const el = Array.isArray(refs) ? refs[rowIdx] : refs;
      if (!el) return;
      const canScrollLeft = el.scrollLeft > 4;
      const canScrollRight = el.scrollLeft + el.clientWidth < el.scrollWidth - 4;
      this.$set(this.rowOverflow, rowIdx, { canScrollLeft, canScrollRight });
    },
    measureAllRowOverflow() {
      this.$nextTick(() => {
        this.localRows.forEach((_, idx) => this.measureRowOverflow(idx));
      });
    },
    onRowScroll(rowIdx) {
      this.measureRowOverflow(rowIdx);
    },
    scrollRow(rowIdx, direction) {
      const refs = this.$refs.rowTiles;
      const el = Array.isArray(refs) ? refs[rowIdx] : refs;
      if (!el) return;
      el.scrollBy({ left: direction * 200, behavior: 'smooth' });
    },
  },
};
</script>

<style lang="scss" scoped>
.pdp-set-picker {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  font-size: 13px;
}
.pdp-set-picker__intro {
  color: #475569;
  margin: 0;
  line-height: 1.5;
}
.pdp-set-picker__intro-icon {
  color: #1a1a2e;
  margin-right: 1px;
  font-size: 0.95em;
}
.pdp-set-picker__preset {
  position: relative;
}
.pdp-set-picker__preset-trigger {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
}
.pdp-set-picker__preset-icon { color: #379C75; flex-shrink: 0; }
.pdp-set-picker__preset-label { font-weight: 600; color: #1a1a2e; flex-shrink: 0; }
.pdp-set-picker__preset-value { flex: 1; color: #94a3b8; font-size: 13px; }
.pdp-set-picker__preset-chevron { color: #94a3b8; flex-shrink: 0; }
.pdp-set-picker__preset-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  max-height: 240px;
  overflow-y: auto;
  z-index: 30;
}
.pdp-set-picker__preset-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 8px 14px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}
.pdp-set-picker__preset-item:hover { background: #f1f5f9; }
.pdp-set-picker__preset-item.is-active { background: #eaf7f2; color: #2d6a55; }
.pdp-set-picker__preset-count { color: #94a3b8; font-weight: 400; }
.pdp-set-picker__bulk {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #f8fafc;
  padding: 8px 12px;
  border-radius: 8px;
}
.pdp-set-picker__bulk-label { color: #64748b; font-size: 12px; font-weight: 500; }
.pdp-set-picker__bulk-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #cbd5e1;
  background: #fff;
  border-radius: 6px;
  font-size: 12px;
  cursor: pointer;
}
.pdp-set-picker__bulk-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__rows { display: flex; flex-direction: column; }
.pdp-set-picker__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 0;
  border-top: 1px solid #f1f5f9;
}
.pdp-set-picker__row:first-child { border-top: 1px solid #e2e8f0; }
.pdp-set-picker__row-actions {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex-shrink: 0;
  margin-right: 6px;
  opacity: 0;
  transition: opacity 0.15s;
}
.pdp-set-picker__row:hover .pdp-set-picker__row-actions {
  opacity: 1;
}
.pdp-set-picker__row-action {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #94a3b8;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 11px;
  padding: 0;
  transition: background 0.15s, color 0.15s, opacity 0.15s;
}
.pdp-set-picker__row-action:hover:not(:disabled) {
  background: #e2e8f0;
  color: #1a1a2e;
}
.pdp-set-picker__row-action:disabled {
  cursor: not-allowed;
  opacity: 0.35;
}
.pdp-set-picker__row-action--remove:hover:not(:disabled) {
  background: #fee2e2;
  color: #b91c1c;
}
.pdp-set-picker__row-label {
  width: 110px;
  font-weight: 600;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
}
.drag-handle { cursor: grab; }
.pdp-set-picker__row-tiles-wrapper {
  position: relative;
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
}
/* Pushes the trailing per-row controls (the direction wand) to the far right
   while the "+" sits immediately after the tiles. (POR-13271) */
.pdp-set-picker__row-spacer {
  flex: 1 1 auto;
  min-width: 8px;
}
.pdp-set-picker__wand-wrap {
  position: relative;
  flex-shrink: 0;
  align-self: center;
}
.pdp-set-picker__wand {
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.pdp-set-picker__row:hover .pdp-set-picker__wand,
.pdp-set-picker__wand.is-active,
.pdp-set-picker__wand.is-open {
  opacity: 1;
}
/* Has direction text: "partly filled" — light green tint + the green dot, NOT a
   solid fill (the solid green is reserved for hover). (POR-13271 feedback #2) */
.pdp-set-picker__wand.is-active {
  background: #eaf7f2;
  border-color: #379C75;
  color: #2d6a55;
}
/* Hover: solid green fill. (feedback #1) */
.pdp-set-picker__wand:hover:not(:disabled) {
  background: #379C75;
  border-color: #379C75;
  color: #fff;
}
.pdp-set-picker__wand.is-open {
  border-color: #1ca9e3;
  box-shadow: 0 0 0 3px rgba(28, 169, 227, 0.25);
}
.pdp-set-picker__wand:disabled {
  cursor: not-allowed;
}
.pdp-set-picker__wand-dot {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  border: 1.5px solid #fff;
}
.pdp-set-picker__direction {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 385px;
  z-index: 40;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  padding: 14px;
  cursor: default;
}
.pdp-set-picker__direction.is-above {
  top: auto;
  bottom: calc(100% + 6px);
}
.pdp-set-picker__direction-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.pdp-set-picker__direction-title {
  font-weight: 700;
  font-size: 12px;
  color: #1a1a2e;
  font-family: ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
}
.pdp-set-picker__direction-optional { color: #64748b; font-size: 10px; }
.pdp-set-picker__direction-text {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 5px;
  padding: 8px 10px;
  font-size: 13px;
  resize: vertical;
  color: #334155;
  font-family: inherit;
  background: #f8fafc;
}
.pdp-set-picker__direction-text:focus {
  outline: none;
  border: 2px solid #189a73;
}
.pdp-set-picker__direction-foot {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 4px;
}
.pdp-set-picker__direction-note { color: #94a3b8; font-size: 10px; }
.pdp-set-picker__direction-clear {
  background: none;
  border: none;
  color: #475569;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}
.pdp-set-picker__direction-clear:hover { color: #1a1a2e; text-decoration: underline; }
.pdp-set-picker__yours-wrap {
  position: relative;
  flex-shrink: 0;
  display: none;
}
/* "Yours" is a secondary option: hidden until the row is hovered, but kept
   visible once an image has been picked/uploaded (mode === 'yours') or while
   its menu is open so it survives the mouse leaving. (POR-13364) */
.pdp-set-picker__row:hover .pdp-set-picker__yours-wrap,
.pdp-set-picker__yours-wrap.is-selected,
.pdp-set-picker__yours-wrap.is-menu-open {
  display: block;
}
.pdp-set-picker__tile--yours {
  background: #fff;
  border: 1px dashed #cbd5e1;
  color: #64748b;
  transition: box-shadow 0.15s, border-color 0.15s;
}
/* Light shadow on hover to emphasize it. (POR-13271 feedback #3) */
.pdp-set-picker__tile--yours:hover {
  border-color: #94a3b8;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.14);
}
.pdp-set-picker__tile--yours > i:not(.pdp-set-picker__tile-check) { font-size: 18px; }
.pdp-set-picker__yours-menu {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  width: 240px;
  z-index: 40;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.12);
  padding: 6px;
}
.pdp-set-picker__yours-menu-head {
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.04em;
  padding: 8px 10px 4px;
}
.pdp-set-picker__yours-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #334155;
  text-align: left;
}
.pdp-set-picker__yours-menu-item:hover { background: #f1f5f9; }
.pdp-set-picker__yours-menu-item:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__yours-menu-item i { color: #64748b; }
.pdp-set-picker__row-tiles {
  display: flex;
  align-items: center;
  gap: 6px;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
}
.pdp-set-picker__row-chevron {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: none;
  background: #379C75;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  cursor: pointer;
  z-index: 5;
  transition: opacity 0.15s;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  font-size: 11px;
  padding: 0;
}
.pdp-set-picker__row-chevron i { line-height: 1; }
.pdp-set-picker__row-chevron--left { left: 4px; }
.pdp-set-picker__row-chevron--right { right: 4px; }
.pdp-set-picker__row:hover .pdp-set-picker__row-chevron {
  opacity: 1;
}
.pdp-set-picker__tile {
  position: relative;
  width: 64px;
  height: 64px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 11px;
  gap: 4px;
  color: #64748b;
  cursor: pointer;
  padding: 0;
  overflow: hidden;
}
.pdp-set-picker__tile img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.pdp-set-picker__tile.is-selected {
  border-color: #379C75;
  border-width: 2px;
}
.pdp-set-picker__tile.is-disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.pdp-set-picker__tile--ai { background: #eaf7f2; color: #2d6a55; }
.pdp-set-picker__tile--ai-ref { background: #fff; }
.pdp-set-picker__tile-ref-img {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
}
.pdp-set-picker__tile-ref-badge {
  position: absolute;
  left: 4px;
  bottom: 4px;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 1px 6px;
  border-radius: 10px;
  background: rgba(45, 106, 85, 0.92);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
}
.pdp-set-picker__tile--scene { background: #ecfdf5; color: #047857; }
.pdp-set-picker__tile--original { background: #eaf7f2; color: #2d6a55; }
.pdp-set-picker__tile-original-label {
  position: absolute;
  bottom: 3px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 9px;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.92);
  padding: 0 5px;
  border-radius: 8px;
  color: #2d6a55;
  line-height: 1.5;
}
.pdp-set-picker__original-note {
  color: #94a3b8;
  font-style: italic;
  font-size: 12px;
  margin-left: 8px;
  flex-shrink: 0;
}
.pdp-set-picker__tile--add {
  width: 28px;
  height: 28px;
  align-self: center;
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 18px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity 0.15s, color 0.15s;

  &:hover:not(:disabled) {
    color: #64748b;
  }

  &:disabled {
    cursor: not-allowed;
  }
}
.pdp-set-picker__row:hover .pdp-set-picker__tile--add { opacity: 1; }
/* Hero "alternate" choices (AI tile + Yours tile): hidden until the row is
   hovered, dimmed while not the active choice, fully shown when selected (or
   while the Yours menu is open, so it survives the mouse leaving). (POR-13271) */
.pdp-set-picker__tile--hero-alt {
  display: none;
  opacity: 0.5;
  transition: opacity 0.15s;
}
.pdp-set-picker__row:hover .pdp-set-picker__tile--hero-alt,
.pdp-set-picker__tile--hero-alt.is-selected {
  display: flex;
}
.pdp-set-picker__tile--hero-alt.is-selected,
.pdp-set-picker__tile--hero-alt:hover {
  opacity: 1;
}
.pdp-set-picker__hero-alt-wrap {
  display: none;
  opacity: 0.5;
  transition: opacity 0.15s;
}
.pdp-set-picker__row:hover .pdp-set-picker__hero-alt-wrap,
.pdp-set-picker__hero-alt-wrap.is-selected,
.pdp-set-picker__hero-alt-wrap.is-menu-open {
  display: block;
}
.pdp-set-picker__hero-alt-wrap.is-selected,
.pdp-set-picker__hero-alt-wrap:hover {
  opacity: 1;
}
.pdp-set-picker__tile--ai > i:not(.pdp-set-picker__tile-check),
.pdp-set-picker__tile--scene > i:not(.pdp-set-picker__tile-check) {
  font-size: 18px;
}
.pdp-set-picker__tile span {
  font-size: 11px;
  font-weight: 500;
}
.pdp-set-picker__tile-check {
  position: absolute;
  top: 4px;
  right: 4px;
  color: #379C75;
  background: #fff;
  border-radius: 50%;
  font-size: 18px;
  line-height: 1;
  width: 18px;
  height: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.pdp-set-picker__tile-slot {
  position: relative;
  flex-shrink: 0;
  display: inline-flex;
}
.pdp-set-picker__tile-expand {
  position: absolute;
  top: 4px;
  left: 4px;
  background: rgba(255, 255, 255, 0.85);
  border: none;
  border-radius: 4px;
  width: 18px;
  height: 18px;
  display: none;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  cursor: pointer;
}
.pdp-set-picker__tile--tpl:hover .pdp-set-picker__tile-expand,
.pdp-set-picker__tile-slot:hover .pdp-set-picker__tile-expand,
.pdp-set-picker__yours-wrap:hover .pdp-set-picker__tile-expand {
  display: flex;
}
.pdp-set-picker__preview-lightbox {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: rgba(15, 23, 42, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.pdp-set-picker__preview-close {
  position: absolute;
  top: 18px;
  right: 22px;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  font-size: 16px;
  cursor: pointer;
  color: #111827;
}
.pdp-set-picker__preview-img {
  display: block;
  max-width: 88vw;
  max-height: 88vh;
  width: auto;
  height: auto;
  border-radius: 8px;
}
.pdp-set-picker__add-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #64748b;
  margin-top: 4px;
}
.pdp-set-picker__add-label { font-weight: 600; color: #475569; margin-right: 4px; }
.pdp-set-picker__chip {
  padding: 3px 9px;
  border: 1px dashed #cbd5e1;
  background: transparent;
  border-radius: 12px;
  color: #64748b;
  font-size: 11px;
  cursor: pointer;
}
.pdp-set-picker__chip:hover { background: #f8fafc; color: #475569; }
.pdp-set-picker__footer {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 8px;
}
.pdp-set-picker__create {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #379C75;
  border-color: #379C75;
  color: #fff;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
}
.pdp-set-picker__create:hover:not(:disabled) {
  background: #2E8D68;
  border-color: #2E8D68;
}
/* Highlight only on click, not by default (POR-13596) */
.pdp-set-picker__create:active:not(:disabled) {
  box-shadow: 0 0 0 3px rgba(55, 156, 117, 0.25);
}
.pdp-set-picker__create i {
  font-size: 0.95em;
  line-height: 1;
}
.pdp-set-picker__create-sequential {
  background: #fff;
  border: 1px solid #cbd5e1;
  color: #475569;
  padding: 8px 14px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
}
.pdp-set-picker__create-sequential:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #94a3b8;
}
.pdp-set-picker__create-sequential:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
.pdp-set-picker__browse {
  background: transparent;
  border: none;
  padding: 0;
  color: #379C75;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
}
.pdp-set-picker__browse:hover { text-decoration: underline; }
.pdp-set-picker__browse:disabled { cursor: not-allowed; opacity: 0.5; }
.pdp-set-picker__direction-label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #1a1a2e;
  font-family: ui-sans-serif, system-ui, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol', 'Noto Color Emoji';
}
.pdp-set-picker__direction-section {
  margin-top: 14px;
}
.pdp-set-picker__direction-section-head {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 13px;
  margin-bottom: 8px;
}
.pdp-set-picker__direction-section-note {
  color: #94a3b8;
  font-size: 10px;
}
.pdp-set-picker__logo-btn {
  width: 100%;
  height: 36px;
  padding: 10px;
  border: 1px dashed #cbd5e1;
  border-radius: 4px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: border-color 0.15s, background 0.15s;
}
.pdp-set-picker__logo-btn:hover:not(:disabled) {
  color: #0f172a;
  background: #189a73;
}
.pdp-set-picker__logo-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__logo-remove {
  margin-left: auto;
  background: none;
  border: none;
  color: #64748b;
  font-size: 10px;
  font-weight: 500;
  cursor: pointer;
  padding: 0;
}
.pdp-set-picker__logo-remove:hover { color: #ef4444; }
.pdp-set-picker__logo-selected {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  background: #f8fafc;
}
.pdp-set-picker__logo-thumb-sm {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 4px;
  flex-shrink: 0;
}
.pdp-set-picker__logo-name {
  flex: 1;
  font-size: 12px;
  color: #334155;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
}
.pdp-set-picker__logo-change {
  background: none;
  border: none;
  color: #379C75;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
}
.pdp-set-picker__logo-change:hover { text-decoration: underline; }
.pdp-set-picker__logo-change:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__direction-done {
  background: #189a73;
  color: #fff;
  border: none;
  border-radius: 5px;
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.pdp-set-picker__direction-done:hover { background: #189a73e6; }

.pdp-set-picker__add-image-type {
  position: relative;
}
.pdp-set-picker__add-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
  color: #64748b;
  font-size: 13px;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.pdp-set-picker__add-trigger:hover:not(:disabled) {
  color: #0f172a;
  border: 1px dashed #cbd5e1;
  background: #189a73;
}
.pdp-set-picker__add-trigger.is-open {
  border-color: #94a3b8;
  background: #f1f5f9;
  color: #334155;
}
.pdp-set-picker__add-trigger:disabled { opacity: 0.5; cursor: not-allowed; }
.pdp-set-picker__add-chevron {
  font-size: 9px;
  margin-left: 2px;
  transition: transform 0.15s;
}
.pdp-set-picker__add-trigger.is-open .pdp-set-picker__add-chevron {
  transform: rotate(180deg);
}
.pdp-set-picker__add-dropdown-menu {
  position: absolute;
  bottom: calc(100% + 4px);
  left: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
  min-width: 180px;
  max-height: fit-content;
  overflow-y: auto;
  z-index: 30;
  padding: 4px;
}
.pdp-set-picker__add-item {
  display: flex;
  align-items: center;
  width: 100%;
  padding: 7px 12px;
  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 13px;
  color: #334155;
  text-align: left;
  white-space: nowrap;
}
.pdp-set-picker__add-item:hover { background: #f1f5f9; }
.separator {
  border: 1px solid #f1f5f9;
  margin: 10px 0 0 0;
}
.pdp-set-picker__overlay-mode {
  display: flex;
  gap: 4px;
  margin-top: 6px;
}
.pdp-set-picker__overlay-mode-btn {
  padding: 4px 8px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #f8fafc;
  color: #475569;
  font-size: 11px;
  cursor: pointer;
  text-align: center;
}
.pdp-set-picker__overlay-mode-btn input[type="radio"] {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
}

.pdp-set-picker__overlay-mode-btn.is-active {
  background: #1f8a6d;
  border-color: #1f8a6d;
  color: #fff;
}

.pdp-set-picker__overlay-mode-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
